import React, { useMemo } from "react";
import { emojiDecode } from "../utils/encryptDecryptFunction";
import "./EmojiReactionsDisplay.css";

// Reaction chips under a chat bubble (mobile: ProMessageRow.ProReactions):
// 26px pills with the emoji and the number of people who reacted with it (always shown).
// A chip I reacted with is outlined in the accent. Click toggles my own reaction with that
// emoji; right-click (or Alt+click) lists who reacted.

// Ensure single-codepoint symbols (e.g. the heart U+2764) render as the
// colored emoji rather than the monochrome text glyph by appending the
// emoji variation selector when it isn't already present.
const VARIATION_SELECTOR = "️";
const toColorEmoji = (emoji) => {
  if (!emoji) return emoji;
  if (emoji.includes(VARIATION_SELECTOR)) return emoji;
  return Array.from(emoji).length === 1 ? emoji + VARIATION_SELECTOR : emoji;
};

/** [{ key, glyph, count (distinct senders), mine }] in first-reaction order. */
export function groupReactions(list, myId) {
  if (!Array.isArray(list) || !list.length) return [];
  const groups = new Map();
  list.forEach((r) => {
    if (!r || r.emoji == null) return;
    const key = String(r.emoji);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(r);
  });
  return Array.from(groups.entries()).map(([key, rows]) => {
    const senders = new Set(rows.map((r, i) => (r && r.sender != null ? String(r.sender) : `anon-${i}`)));
    let glyph = key;
    try {
      glyph = toColorEmoji(emojiDecode(key));
    } catch (_) {
      glyph = key;
    }
    return { key, glyph, count: senders.size, mine: myId != null && senders.has(String(myId)) };
  });
}

const EmojiReactionsDisplay = React.memo(({
  userEmoji,
  isOwn,
  myId,
  groupId,
  chatId,
  onToggle,
  onShowReactions,
  disabled = false,
  showReactionsLabel,
}) => {
  const groups = useMemo(() => groupReactions(userEmoji, myId), [userEmoji, myId]);
  if (!groups.length) return null;

  const showWho = (e) => {
    if (!onShowReactions) return;
    e.preventDefault();
    e.stopPropagation();
    onShowReactions({ emojiList: userEmoji, groupId, chatId, modalIs: true });
  };

  return (
    <div className={`emoji-reaction-container kjc-reacts ${isOwn ? "emoji-reaction-right" : "emoji-reaction-left"}`}>
      {groups.map((g) => (
        <button
          key={g.key}
          type="button"
          className={`kjc-react${g.mine ? " is-mine" : ""}`}
          aria-pressed={g.mine}
          title={showReactionsLabel}
          onClick={(e) => {
            e.stopPropagation();
            if (e.altKey) {
              showWho(e);
              return;
            }
            if (disabled || !onToggle) {
              showWho(e);
              return;
            }
            onToggle(g.key, g.mine);
          }}
          onContextMenu={showWho}
        >
          <span className="kjc-react__glyph">{g.glyph}</span>
          <span className="kjc-react__count">{g.count}</span>
        </button>
      ))}
    </div>
  );
});

EmojiReactionsDisplay.displayName = "EmojiReactionsDisplay";

export default EmojiReactionsDisplay;
